import { useCallback, useState } from "react"

export type RowSubmitStatus = "idle" | "saving" | "saved" | "error"

export type RowSubmitState = {
  status: RowSubmitStatus
  errors: string[]
  serverId?: number
}

type SaveResult<T> = { id: number; serverRow?: T } | { errors: string[] }

type Options<TRow> = {
  // Returns `null` if a row should be skipped (incomplete).
  // Returns array of error messages if invalid.
  // Returns [] if valid and ready to save.
  validate: (row: TRow) => string[] | null
  create: (row: TRow) => Promise<SaveResult<TRow>>
  update: (row: TRow & { id: number }) => Promise<SaveResult<TRow>>
}

// Explicit-save flow: caller holds the rows, hits Submit when ready, hook
// walks every row and POSTs/PUTs as appropriate. Returns per-row state so the
// grid can mark errors in the UI.
export function useGridSubmit<TRow extends { id?: number }>(
  options: Options<TRow>,
) {
  const { validate, create, update } = options
  const [states, setStates] = useState<Record<string, RowSubmitState>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const setRowState = useCallback(
    (rowKey: string, patch: Partial<RowSubmitState>) => {
      setStates((prev) => ({
        ...prev,
        [rowKey]: { ...(prev[rowKey] ?? { status: "idle", errors: [] }), ...patch },
      }))
    },
    [],
  )

  const submitAll = useCallback(
    async (
      rows: { key: string; row: TRow }[],
    ): Promise<{
      ok: boolean
      savedCount: number
      skippedCount: number
      updated: { key: string; serverId: number }[]
    }> => {
      setIsSubmitting(true)
      const updated: { key: string; serverId: number }[] = []
      let allOk = true
      let savedCount = 0
      let skippedCount = 0

      try {
        for (const { key, row } of rows) {
          const validation = validate(row)
          if (validation === null) {
            // Skip incomplete rows — they stay local-only.
            skippedCount += 1
            setRowState(key, { status: "idle", errors: [] })
            continue
          }
          if (validation.length > 0) {
            allOk = false
            setRowState(key, { status: "error", errors: validation })
            continue
          }

          setRowState(key, { status: "saving", errors: [] })
          try {
            const result =
              row.id != null
                ? await update(row as TRow & { id: number })
                : await create(row)
            if ("errors" in result) {
              allOk = false
              setRowState(key, { status: "error", errors: result.errors })
            } else {
              savedCount += 1
              updated.push({ key, serverId: result.id })
              setRowState(key, {
                status: "saved",
                errors: [],
                serverId: result.id,
              })
            }
          } catch (err) {
            allOk = false
            const message = err instanceof Error ? err.message : String(err)
            setRowState(key, { status: "error", errors: [message] })
          }
        }
      } finally {
        setIsSubmitting(false)
      }
      return { ok: allOk, savedCount, skippedCount, updated }
    },
    [create, setRowState, update, validate],
  )

  const clearRowState = useCallback((rowKey: string) => {
    setStates((prev) => {
      const { [rowKey]: _, ...rest } = prev
      return rest
    })
  }, [])

  return { states, isSubmitting, submitAll, clearRowState }
}
